import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SubCtx = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [hasActive, setHasActive] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [trialEndAt, setTrialEndAt] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);
  const [trialExpired, setTrialExpired] = useState(false);
  const [profitMargin, setProfitMargin] = useState(3);
  const [loading, setLoading] = useState(true);
  const [fetchedForUser, setFetchedForUser] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setSubscription(null); setHasActive(false);
      setIsTrial(false); setTrialEndAt(null); setTrialDaysLeft(0); setTrialExpired(false);
      setLoading(authLoading);
      setFetchedForUser(null);
      return;
    }
    // Ensure gates block routing until the fresh subscription state is fetched
    // (prevents SubGate from bouncing a just-logged-in trial user to /pricing).
    setLoading(true);
    try {
      const r = await api.get("/billing/subscription");
      setSubscription(r.data.subscription);
      setHasActive(!!r.data.has_active);
      setIsTrial(!!r.data.is_trial);
      setTrialEndAt(r.data.trial_end_at || null);
      setTrialDaysLeft(Number(r.data.trial_days_left || 0));
      setTrialExpired(!!r.data.trial_expired);
    } catch {
      setSubscription(null);
      setHasActive(false);
      setIsTrial(false); setTrialEndAt(null); setTrialDaysLeft(0); setTrialExpired(false);
    }
    // Profit margin — public, tolerant to 402.
    try {
      const pm = await api.get("/settings/profit-margin");
      setProfitMargin(Number(pm.data?.percent ?? 3));
    } catch { /* keep default */ }
    setFetchedForUser(user.id);
    setLoading(false);
  }, [user, authLoading]);

  useEffect(() => { refresh(); }, [refresh]);

  // Race guard: if the user just changed (login/logout) but refresh hasn't
  // completed for THIS user yet, treat as loading so gates don't redirect
  // to /pricing on stale hasActive=false.
  const effectiveLoading = loading || (!!user && fetchedForUser !== user.id);

  return (
    <SubCtx.Provider value={{ subscription, hasActive, isTrial, trialEndAt, trialDaysLeft, trialExpired, profitMargin, setProfitMargin, loading: effectiveLoading, refresh }}>
      {children}
    </SubCtx.Provider>
  );
}

export const useSubscription = () => useContext(SubCtx);
